import type { RegisteredRouter, RouteIds } from "@tanstack/react-router"
import { useNavigate, useSearch } from "@tanstack/react-router"
import type { SortingState, Updater } from "@tanstack/react-table"
import { useCallback, useEffect, useMemo, useRef } from "react"
import type { RefObject } from "react"
import type { Client } from "urql"

import { SortingOrder } from "@/graphql/codegen/graphql"
import { useScrollLoadMore } from "@/hooks/useScrollLoadMore"
import { useThrottle } from "@/hooks/useThrottle"
import { queryClient } from "@/providers/GraphqlRouterProvider"
import useDebounce from "@/utils/useDebounce"
import { toUpperSnakeCase, upperSnakeCaseToCamelCase } from "@/utils/utils"

interface BaseSearchParams {
  query?: string
  field?: string
  order?: SortingOrder
}

export interface ListPageStoreSelectors<TData, TParams extends BaseSearchParams> {
  data: TData[]
  fetching: boolean
  error: Error | undefined
  executeSearch: (client: Client, params: TParams, force?: boolean) => void
  loadMore: (client: Client, params: TParams) => void
  clearStore: () => void
}

interface UseListPageBaseParams<TData, TParams extends BaseSearchParams> {
  route: RouteIds<RegisteredRouter["routeTree"]>
  store: ListPageStoreSelectors<TData, TParams>
  filterKeys?: (keyof TParams)[]
  preservedEmptyFilterKeys?: (keyof TParams)[]
}

export interface UseListPageBaseReturn<TData, TParams extends BaseSearchParams> {
  data: TData[]
  isFetching: boolean
  error: Error | undefined
  sorting: SortingState
  searchParams: TParams
  tableContainerRef: RefObject<HTMLDivElement>
  handleScroll: () => void
  handleSortingChange: (updater: Updater<SortingState>) => void
  handleSearchChange: (query: string) => void
  handleFilterChange: <K extends keyof TParams>(key: K, value: TParams[K]) => void
  handleFilterRemove: (key: keyof TParams, value?: string | number) => void
  handleFilterClear: () => void
}

export const useListPageBase = <TData, TParams extends BaseSearchParams>({
  route,
  store,
  filterKeys = [],
  preservedEmptyFilterKeys = [],
}: UseListPageBaseParams<TData, TParams>): UseListPageBaseReturn<TData, TParams> => {
  const { data, fetching, error, executeSearch, loadMore, clearStore } = store

  const tableContainerRef = useRef<HTMLDivElement>(null)

  const searchParams = useSearch({ from: route })
  const { field, order } = searchParams

  const [throttledLoadMore] = useThrottle(() => loadMore(queryClient, searchParams), 200)

  const handleScroll = useScrollLoadMore({
    fetching,
    loadMore: throttledLoadMore,
    scrollContainerRef: tableContainerRef,
  })

  const navigate = useNavigate({ from: route as any })

  const debouncedQuery = useDebounce(searchParams.query, 300)

  useEffect(() => {
    executeSearch(queryClient, { ...searchParams, query: debouncedQuery })
  }, [executeSearch, searchParams, debouncedQuery])

  useEffect(() => {
    return () => {
      clearStore()
    }
  }, [clearStore])

  const sorting: SortingState = useMemo(
    () => (field ? [{ id: upperSnakeCaseToCamelCase(field), desc: order === SortingOrder.Desc }] : []),
    [field, order]
  )

  const handleSortingChange = useCallback(
    (updater: Updater<SortingState>) => {
      const newSorting = typeof updater === "function" ? updater(sorting) : updater

      if (!newSorting.length) return

      const sort = newSorting[0]
      ;(navigate as any)({
        search: (prev: Record<string, any>) => ({
          ...prev,
          field: toUpperSnakeCase(sort.id),
          order: sort.desc ? SortingOrder.Desc : SortingOrder.Asc,
        }),
        replace: true,
      })
    },
    [navigate, sorting]
  )

  const handleSearchChange = useCallback(
    (query: string) => {
      ;(navigate as any)({
        search: (prev: Record<string, any>) => ({
          ...prev,
          query: query || undefined,
        }),
        replace: true,
      })
    },
    [navigate]
  )

  const handleFilterChange = useCallback(
    <K extends keyof TParams>(key: K, value: TParams[K]) => {
      const isEmpty = Array.isArray(value) ? value.length === 0 : value === null || value === undefined
      ;(navigate as any)({
        search: (prev: Record<string, any>) => ({
          ...prev,
          [key]: preservedEmptyFilterKeys.includes(key) || !isEmpty ? value : undefined,
        }),
        replace: true,
      })
    },
    [navigate, preservedEmptyFilterKeys]
  )

  const handleFilterRemove = useCallback(
    (key: keyof TParams, value?: string | number) => {
      const currentValue = searchParams[key]
      const updatedValue =
        Array.isArray(currentValue) && value !== undefined ? currentValue.filter((item) => item !== value) : undefined
      const isEmpty = Array.isArray(updatedValue) ? updatedValue.length === 0 : !updatedValue
      ;(navigate as any)({
        search: (prev: Record<string, any>) => ({
          ...prev,
          [key]: preservedEmptyFilterKeys.includes(key) || !isEmpty ? updatedValue : undefined,
        }),
        replace: true,
      })
    },
    [navigate, searchParams, preservedEmptyFilterKeys]
  )

  const handleFilterClear = useCallback(() => {
    const clearedFilters = filterKeys.reduce<Record<string, undefined>>(
      (acc, key) => ({ ...acc, [key as string]: undefined }),
      {}
    )
    ;(navigate as any)({
      search: (prev: Record<string, any>) => ({
        ...prev,
        ...clearedFilters,
      }),
      replace: true,
    })
  }, [navigate, filterKeys])

  return {
    data,
    isFetching: fetching,
    error,
    sorting,
    searchParams,
    tableContainerRef,
    handleScroll,
    handleSortingChange,
    handleSearchChange,
    handleFilterChange,
    handleFilterRemove,
    handleFilterClear,
  }
}
